import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { AuthService } from '../../../services/auth.service';
import { NotificationService } from '../../../services/notification.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';

interface User {
  id: number;
  username: string;
  status: string;
  employee: {
    id: number;
    name: string;
    employee_code: string;
    email: string;
    mobile_number: string;
    profile_photo?: string;
    role: {
      id: number;
      name: string;
      slug: string;
    };
  };
}

@Component({
  selector: 'app-profile-popup',
  templateUrl: './profile-popup.component.html',
  styleUrls: ['./profile-popup.component.scss']
})
export class ProfilePopupComponent implements OnInit, OnChanges {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();

  currentUser: User | null = null;
  loading = false;
  
  // Form data
  editMode = false;
  newName = '';
  newEmail = '';
  newMobile = '';
  
  // Password reset
  showPasswordReset = false;
  currentPassword = '';
  newPassword = '';
  confirmPassword = '';
  
  // Profile image
  selectedFile: File | null = null;
  profileImagePreview: string | null = null;

  constructor(
    private authService: AuthService,
    private notificationService: NotificationService,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.currentUser = this.authService.getCurrentUser();
    if (this.currentUser) {
      this.newName = this.currentUser.employee.name;
      this.newEmail = this.currentUser.employee.email;
      this.newMobile = this.currentUser.employee.mobile_number;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle input changes if needed
  }

  /**
   * Close the popup
   */
  closePopup() {
    this.editMode = false;
    this.showPasswordReset = false;
    this.resetForm();
    this.close.emit();
  }

  /**
   * Toggle edit mode
   */
  toggleEditMode() {
    this.editMode = !this.editMode;
    if (!this.editMode) {
      this.resetForm();
    }
  }

  /**
   * Toggle password reset form
   */
  togglePasswordReset() {
    this.showPasswordReset = !this.showPasswordReset;
    if (!this.showPasswordReset) {
      this.resetPasswordForm();
    }
  }

  /**
   * Reset form to original values
   */
  resetForm() {
    if (this.currentUser) {
      this.newName = this.currentUser.employee.name;
      this.newEmail = this.currentUser.employee.email;
      this.newMobile = this.currentUser.employee.mobile_number;
    }
    this.selectedFile = null;
    this.profileImagePreview = null;
  }

  /**
   * Reset password form
   */
  resetPasswordForm() {
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
  }

  /**
   * Handle file selection for profile image
   */
  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.notificationService.showError('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.notificationService.showError('Image size should not exceed 5MB');
        return;
      }
      
      this.selectedFile = file;
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.profileImagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  /**
   * Remove selected profile image
   */
  removeProfileImage() {
    this.selectedFile = null;
    this.profileImagePreview = null;
  }

  /**
   * Update profile information
   */
  updateProfile() {
    if (!this.currentUser) return;
    
    // Validate form fields
    if (!this.newName || !this.newEmail || !this.newMobile) {
      this.notificationService.showError('Please fill in all required fields');
      return;
    }
    
    this.loading = true;
    
    const profileData = {
      name: this.newName,
      email: this.newEmail,
      mobile_number: this.newMobile
    };
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.authService.getToken()}`
    });
    
    this.http.put(`${environment.apiBaseUrl}/profile/update`, profileData, { headers }).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response.success) {
          this.notificationService.showSuccess('Profile updated successfully!');
          this.editMode = false;
          this.resetForm();
          // Refresh user data
          this.authService.refreshUserFromToken();
        } else {
          this.notificationService.showError(response.message || 'Failed to update profile');
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('Error updating profile:', error);
        
        // Extract error message from API response
        let errorMessage = 'Failed to update profile';
        if (error.error && error.error.message) {
          errorMessage = error.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Handle validation errors
        if (error.error && error.error.errors) {
          const validationErrors = error.error.errors;
          const errorMessages = [];
          for (const field in validationErrors) {
            if (validationErrors[field]) {
              errorMessages.push(validationErrors[field].join(', '));
            }
          }
          if (errorMessages.length > 0) {
            errorMessage = errorMessages.join('; ');
          }
        }
        
        this.notificationService.showError(errorMessage);
      }
    });
  }

  /**
   * Reset password
   */
  resetPassword() {
    if (this.newPassword !== this.confirmPassword) {
      this.notificationService.showError('New passwords do not match');
      return;
    }
    
    if (this.newPassword.length < 6) {
      this.notificationService.showError('Password must be at least 6 characters long');
      return;
    }
    
    this.loading = true;
    
    const passwordData = {
      current_password: this.currentPassword,
      new_password: this.newPassword,
      new_password_confirmation: this.confirmPassword
    };
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.authService.getToken()}`
    });
    
    this.http.put(`${environment.apiBaseUrl}/profile/reset-password`, passwordData, { headers }).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response.success) {
          this.notificationService.showSuccess('Password updated successfully!');
          this.showPasswordReset = false;
          this.resetPasswordForm();
        } else {
          this.notificationService.showError(response.message || 'Failed to update password');
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('Error updating password:', error);
        
        // Extract error message from API response
        let errorMessage = 'Failed to update password';
        if (error.error && error.error.message) {
          errorMessage = error.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Handle validation errors
        if (error.error && error.error.errors) {
          const validationErrors = error.error.errors;
          const errorMessages = [];
          for (const field in validationErrors) {
            if (validationErrors[field]) {
              errorMessages.push(validationErrors[field].join(', '));
            }
          }
          if (errorMessages.length > 0) {
            errorMessage = errorMessages.join('; ');
          }
        }
        
        this.notificationService.showError(errorMessage);
      }
    });
  }

  /**
   * Get user initials
   */
  getUserInitials(): string {
    if (!this.currentUser || !this.currentUser.employee || !this.currentUser.employee.name) return '?';
    
    const name = this.currentUser.employee.name.trim();
    const words = name.split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  /**
   * Get user profile image
   */
  getUserProfileImage(): string {
    return (this.currentUser && this.currentUser.employee && this.currentUser.employee.profile_photo) || '';
  }

  /**
   * Check if user has profile image
   */
  hasProfileImage(): boolean {
    return !!(this.currentUser && this.currentUser.employee && this.currentUser.employee.profile_photo);
  }

  /**
   * Handle profile image error
   */
  onProfileImageError(event: any) {
    // Hide the image - the initials will automatically show due to *ngIf condition
    event.target.style.display = 'none';
  }
}
